import * as React from "react"
import { Link } from "@inertiajs/react"
import {
  Archive,
  ArrowUpRight,
  CalendarCheck,
  Inbox,
  Mail,
  Menu,
  Settings,
  Star,
  Trophy,
  X,
  Zap,
} from "lucide-react"
import { ArmedIndicator } from "@/components/ArmedIndicator"
import { TWENTY_CRM_URL, UserMenu } from "@/components/MainNav"
import { Badge } from "@/components/ui/badge"
import { inboxFilterParams } from "@/components/inbox/helpers"
import type { InboxCounts, InboxFilters, InboxFolder } from "@/components/inbox/types"
import { cn } from "@/lib/utils"

const BRAND = "ReplyOps"

const FOLDERS: readonly {
  readonly value: InboxFolder
  readonly label: string
  readonly icon: React.ComponentType<{ className?: string }>
}[] = [
  { value: "inbox", label: "Inbox", icon: Inbox },
  { value: "needs_reply", label: "Needs reply", icon: Zap },
  { value: "responded", label: "Responded", icon: Mail },
  { value: "positive", label: "Positive", icon: Star },
  { value: "won", label: "Won", icon: Trophy },
  { value: "archived", label: "Archived", icon: Archive },
]

// Folder navigation keeps the panel filters (client/type/lane) but clears the
// search (search is global, so browsing a folder means leaving the search) and
// the rare-status filter (which would otherwise override the folder's scope).
function folderParams(filters: InboxFilters, folder: InboxFolder) {
  return {
    ...inboxFilterParams(filters),
    folder: folder === "inbox" ? undefined : folder,
    status: undefined,
    q: undefined,
  }
}

export function InboxRail({
  counts,
  filters,
}: {
  readonly counts: InboxCounts
  readonly filters: InboxFilters
}) {
  const [mobileOpen, setMobileOpen] = React.useState(false)

  return (
    <>
      <aside className="sticky top-0 z-30 hidden h-dvh w-[200px] shrink-0 flex-col border-r border-hairline bg-surface lg:flex">
        <RailBody counts={counts} filters={filters} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-surface shadow-xl">
            <RailBody
              counts={counts}
              filters={filters}
              onNavigate={() => setMobileOpen(false)}
              onClose={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* Only the hamburger floats on mobile — the armed indicator lives inside
          the drawer, so it never overlaps the list toolbar's search field. */}
      <div className="fixed right-3 top-2 z-30 flex items-center lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-hairline bg-page text-ink-body hover:bg-surface"
          aria-label="Open folders"
        >
          <Menu className="h-4 w-4" />
        </button>
      </div>
    </>
  )
}

function RailBody({
  counts,
  filters,
  onNavigate,
  onClose,
}: {
  readonly counts: InboxCounts
  readonly filters: InboxFilters
  readonly onNavigate?: () => void
  readonly onClose?: () => void
}) {
  return (
    <>
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-hairline px-3">
        <Link
          href="/inbox"
          className="flex min-w-0 items-center gap-2 text-ink-display no-underline"
          aria-label={BRAND}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-faded font-display text-sm font-semibold text-accent">
            {BRAND.charAt(0)}
          </span>
          <span className="truncate font-display text-sm font-semibold">{BRAND}</span>
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-page hover:text-ink-display"
            aria-label="Close folders"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="border-b border-hairline p-2">
        <ArmedIndicator />
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-y-contain p-2 text-sm">
        {FOLDERS.map((folder) => {
          const active = filters.folder === folder.value && !filters.status && !filters.q
          const Icon = folder.icon
          return (
            <Link
              key={folder.value}
              href="/inbox"
              data={folderParams(filters, folder.value)}
              onClick={onNavigate}
              preserveScroll
              className={cn(
                "flex items-center justify-between rounded-md px-2.5 py-1.5 no-underline",
                active
                  ? "bg-accent-faded font-medium text-accent-display"
                  : "text-ink-body hover:bg-page hover:text-ink-display",
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{folder.label}</span>
              </span>
              <span className={cn("text-xs", active ? "text-accent-display" : "text-ink-muted")}>
                {counts.folders[folder.value] ?? 0}
              </span>
            </Link>
          )
        })}

        <div className="mt-auto pt-2">
          <hr className="mb-2" />
          <Link
            href="/today"
            onClick={onNavigate}
            className="flex items-center justify-between rounded-md px-2.5 py-1.5 text-ink-muted no-underline hover:bg-page hover:text-ink-display"
          >
            <span className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 shrink-0" />
              Today
            </span>
            {counts.today_due > 0 && (
              <Badge tone="signal" className="text-[11px]">
                {counts.today_due} due
              </Badge>
            )}
          </Link>
          <a
            href={TWENTY_CRM_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-ink-muted no-underline hover:bg-page hover:text-ink-display"
          >
            <ArrowUpRight className="h-4 w-4 shrink-0" />
            Twenty CRM
          </a>
          <Link
            href="/settings"
            onClick={onNavigate}
            className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-ink-muted no-underline hover:bg-page hover:text-ink-display"
          >
            <Settings className="h-4 w-4 shrink-0" />
            Settings
          </Link>
        </div>
      </nav>

      <div className="border-t border-hairline p-2">
        <UserMenu open />
      </div>
    </>
  )
}
